import { MetricKind } from "@/hooks/api/use-proving-ground";

/**
 * The platform's monospace face, not the operating system's.
 *
 * Roboto Mono is loaded with the rest of the type (`styles/global.css`) and is what the code
 * highlighter already uses. Leaving it out of the stack meant every figure in the Proving
 * Ground fell through to whatever the OS supplies - SF Mono, Consolas, DejaVu - so the same
 * screen had three different looks depending on who opened it.
 */
export const MONO = "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export type Verdict = "pass" | "review" | "gate" | "none";

function paletteKey(verdict: Verdict): "success" | "warning" | "error" | null {
	if (verdict === "pass") return "success";
	if (verdict === "review") return "warning";
	if (verdict === "gate") return "error";
	return null;
}

/**
 * Verdict colour, defined once for every screen that shows one.
 *
 * The step matters more than it looks. Measured against WCAG on this theme's own palette,
 * `.main` as text on a light card comes out at 2.2–3.8:1 and `.dark` at 3.2–4.8:1 — both
 * below the 4.5:1 a figure this small needs, and warning is the worst of them. Only the 800
 * step clears it. On a dark ground the relationship inverts and the light end is what works.
 *
 * Written with `applyStyles` rather than `palette.mode`, because this theme runs in
 * CSS-variables mode where `mode` is fixed at build time and reports whichever scheme
 * happened to be the default, not the one the viewer is actually looking at.
 */
export function verdictInk(verdict: Verdict) {
	return (theme: any) => {
		const key = paletteKey(verdict);
		if (!key) return { color: theme.vars.palette.text.secondary };
		return {
			// The numeric steps exist because this theme spreads the whole colour ramp into the
			// palette. Falling back to `.dark` keeps this rendering if that ever stops being true.
			color: theme.vars.palette[key][800],
			...theme.applyStyles("dark", { color: theme.vars.palette[key][200] }),
		};
	};
}

/**
 * The same verdict as a pale tint carrying that ink — for pills and swatches.
 *
 * Colour as ground rather than fill: a grid of solid blocks is where the eye stops, and the
 * figures printed on them stop being read as figures.
 */
export function verdictTint(verdict: Verdict) {
	return (theme: any) => {
		const key = paletteKey(verdict);
		if (!key) {
			// Unscored. The obvious choice — `text.disabled` on `action.hover` — measures
			// 2.6:1 in light and 2.4:1 in dark, and is the pill that reads as grey on grey.
			return {
				color: theme.vars.palette.text.secondary,
				backgroundColor: "rgba(var(--mui-palette-common-onBackgroundChannel) / 0.06)",
				...theme.applyStyles("dark", {
					backgroundColor: "rgba(var(--mui-palette-common-onBackgroundChannel) / 0.08)",
				}),
			};
		}
		const channel = theme.vars.palette[key].mainChannel;
		return {
			color: theme.vars.palette[key][800],
			backgroundColor: `rgba(${channel} / 0.12)`,
			...theme.applyStyles("dark", {
				color: theme.vars.palette[key][200],
				backgroundColor: `rgba(${channel} / 0.26)`,
			}),
		};
	};
}

/** Turns a passed/total pair into a verdict. */
export function standingOf(passed: number, failed: number): Verdict {
	if (passed + failed === 0) return "none";
	if (failed === 0) return "pass";
	return passed === 0 ? "gate" : "review";
}

export const METRIC_KIND_LABEL: Record<MetricKind, string> = {
	llm_judge: "LLM judge",
	deterministic: "Deterministic",
	statistical: "Statistical",
};
